import { useQuery } from '@tanstack/react-query';

import useDebounce from '../../../../utilities/customHooks/useDebounce';
import { useScreenRules } from '../../../../rules';
import { IDENTIFY_SETTLE_MS } from '../../constants/code';
import { codeChoicesOf } from '../../domain/channels';
import { identifierKind, identifierProblem } from '../../domain/identifier';
import { isIdentifierRefusal, isTransportFailure, toRefusal } from '../../domain/refusal';
import type { Refusal } from '../../domain/refusal';
import type { AlertChannel, IdentifyResult } from '../../types/domain.types';
import { identifyQueryOptions } from '../queries/identifyQuery';

import { unverifiedKindOf } from './loginFailures';
import { fieldValueOf, typedKindOf } from './loginMachine';
import type { LoginMachine } from './useLoginMachine';
import type { IdentifierView } from './loginView';

export interface IdentifierFieldLogic {
  readonly view: IdentifierView;
  readonly codeChoices: readonly AlertChannel[];
  readonly passwordSet: boolean | null;
  readonly type: (value: string) => void;
}

export interface IdentifierFieldOptions {
  readonly submittable: string | null;
  readonly onField: boolean;
  readonly lookedUp: IdentifyResult | undefined;
  readonly refusal: Refusal | null;
}

const identifyRefusalOf = (error: Error | null): Refusal | null => {
  if (error === null || isTransportFailure(error)) return null;
  const refusal = toRefusal(error);
  return isIdentifierRefusal(refusal) ? refusal : null;
};

export const useIdentifierField = (machine: LoginMachine, { submittable, onField, lookedUp, refusal }: IdentifierFieldOptions): IdentifierFieldLogic => {
  const { state, dispatch } = machine;
  const { field } = state;
  const settle = useDebounce((value: string) => dispatch({ type: 'settled', value }), IDENTIFY_SETTLE_MS);
  const identified = useQuery(identifyQueryOptions(onField ? field.lookup : null));
  const rules = useScreenRules();
  const lookupMatches = field.lookup !== null && field.lookup === submittable;
  const found = lookupMatches ? identified.data : undefined;
  const problemKind = identifierKind(field.value) ?? typedKindOf(field.value);
  const problem = field.attempted && submittable === null ? (problemKind === null ? 'EMPTY' : identifierProblem(problemKind, field.value) ?? 'INVALID') : null;
  const known = found ?? lookedUp;

  return {
    view: {
      value: field.value,
      kind: typedKindOf(field.value),
      problem,
      refusal: refusal ?? (lookupMatches ? identifyRefusalOf(identified.error) : null),
      unverifiedKind: unverifiedKindOf(found),
      problemKey: field.problemKey,
      codeLength: rules?.codeLength ?? null,
    },
    codeChoices: codeChoicesOf(known?.channels ?? null),
    passwordSet: known?.passwordSet ?? null,
    type: (value) => {
      dispatch({ type: 'typed', value });
      settle(fieldValueOf(value));
    },
  };
};
